import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Content, Label } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate, ZerosslOrder } from '../../../types/models/Certificate';
import type {
  ApiResponseCertificateShow,
  ApiResponseCertificateZerosslOrderPair,
} from '../../../types/api/ApiResponseCertificate';
import { copyable } from './displayHelpers';

// Explicitly blessed by the backend: user-driven verify calls are
// non-punitive (they never advance the worker's retry backoff), so polling
// every ~10s while the panel is on screen cannot defer background issuance.
const POLL_INTERVAL_MS = 10_000;

interface ZerosslOrderPanelProps {
  certificate: Certificate;
  order: ZerosslOrder;
  /**
   * Fired after every verify (or resync) that yielded a fresh pair — lets the
   * platform tab patch its list row / the detail page refetch once settled.
   */
  onPairChange?: (certificate: Certificate, order: ZerosslOrder) => void;
  /**
   * The row is gone (verify AND the Show fallback 404ed — deleted from
   * another session). Polling has stopped; parents should drop/refetch it.
   */
  onOrderMissing?: () => void;
}

/**
 * The live view of a ZeroSSL order: DNS-validation instructions plus a
 * "check now" action, self-polling every 10 seconds while the order is
 * pending. The panel owns its copy of the {certificate, order} pair after
 * mount (parents re-key it per certificate id) and reports every fresh pair
 * upward through `onPairChange`.
 *
 * Terminal states render a closing alert instead: issued (success flash —
 * parents typically refetch and unmount the panel), failed/cancelled (the
 * safe `last_error` plus a pointer to the Delete/Cancel cleanup action).
 */
function ZerosslOrderPanel({
  certificate: initialCertificate,
  order: initialOrder,
  onPairChange,
  onOrderMissing,
}: ZerosslOrderPanelProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [certificate, setCertificate] = useState<Certificate>(initialCertificate);
  const [order, setOrder] = useState<ZerosslOrder>(initialOrder);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  // The row was deleted from elsewhere (verify AND Show both 404) — terminal.
  const [isGone, setIsGone] = useState<boolean>(false);
  // Serializes the manual button against the background poll ticks.
  const inFlightRef = useRef<boolean>(false);

  // Adopt a fresher pair handed down by the parent (e.g. the detail page's
  // title-bar Refresh refetching the Show endpoint) — the hook state objects
  // are referentially stable between fetches, so this only fires on real
  // reloads and can never clobber panel-local progress mid-render-cycle.
  useEffect(() => {
    if (isGone) {
      return;
    }
    setCertificate(initialCertificate);
    setOrder(initialOrder);
  }, [initialCertificate, initialOrder, isGone]);

  const applyPair = useCallback(
    (nextCertificate: Certificate, nextOrder: ZerosslOrder, previousState: ZerosslOrder['state']) => {
      setCertificate(nextCertificate);
      setOrder(nextOrder);
      setCheckError(null);
      if (nextOrder.state !== previousState) {
        if (nextOrder.state === 'issued') {
          addNotification({
            title: 'Certificate issued',
            srTitle: 'Success alert',
            description: `${nextCertificate.cn} has been validated and issued by ZeroSSL.`,
            variant: 'success',
          });
          setDrawerOpen(true);
        } else if (nextOrder.state === 'failed') {
          addNotification({
            title: 'Certificate order failed',
            srTitle: 'Error alert',
            description: nextOrder.last_error ?? `The order for ${nextCertificate.cn} failed.`,
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      }
      onPairChange?.(nextCertificate, nextOrder);
    },
    [addNotification, setDrawerOpen, onPairChange],
  );

  // Fallback when verify reports the order is no longer pending (409 — e.g.
  // the background worker issued it between polls): one Show read carries the
  // fresh pair (`zerossl_order` is a sibling of `certificate` there). Returns
  // whether a fresh pair was actually recovered — a deleted row 404s here too.
  const resyncFromShow = useCallback(
    async (previousState: ZerosslOrder['state']): Promise<boolean> => {
      const response = await apiFetch(`/datacenters/certificates/${initialCertificate.id}`);
      const body = (await response.json().catch(() => null)) as ApiResponseCertificateShow | null;
      if (body?.status === 'success' && body.data?.certificate && body.data.zerossl_order) {
        applyPair(body.data.certificate, body.data.zerossl_order, previousState);
        return true;
      }
      return false;
    },
    [apiFetch, initialCertificate.id, applyPair],
  );

  // `manual` = the user pressed the check button (as opposed to a background
  // poll tick): only manual failures escalate to a notification — a transient
  // poll error every 10s must not spam the drawer.
  const runVerify = useCallback(
    async (manual: boolean) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsChecking(true);
      const previousState = order.state;
      const notifyFailure = (description: string) => {
        if (manual) {
          addNotification({
            title: 'Could not check the certificate order',
            srTitle: 'Error alert',
            description,
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      };
      try {
        const response = await apiFetch(`/datacenters/certificates/${initialCertificate.id}/verify`, {
          method: 'POST',
        });
        const body = (await response.json().catch(() => null)) as ApiResponseCertificateZerosslOrderPair | null;
        if (body?.status === 'success' && body.data?.certificate && body.data.zerossl_order) {
          applyPair(body.data.certificate, body.data.zerossl_order, previousState);
          return;
        }
        if (response.status === 409 || response.status === 404) {
          const recovered = await resyncFromShow(previousState);
          if (recovered) {
            return;
          }
          if (response.status === 404) {
            // The row itself is gone (deleted/cancelled from another session)
            // — terminal: stop polling and tell the parent to drop it.
            setIsGone(true);
            notifyFailure('This certificate order no longer exists — it was cancelled or deleted.');
            onOrderMissing?.();
            return;
          }
          setCheckError('The order state changed — refresh the page to see the latest state.');
          return;
        }
        const message =
          body?.message ?? `The check failed (HTTP ${response.status}). It will be retried automatically.`;
        setCheckError(message);
        notifyFailure(message);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'The check failed. It will be retried automatically.';
        setCheckError(message);
        notifyFailure(message);
      } finally {
        inFlightRef.current = false;
        setIsChecking(false);
      }
    },
    [apiFetch, initialCertificate.id, order.state, applyPair, resyncFromShow, onOrderMissing, addNotification, setDrawerOpen],
  );

  useEffect(() => {
    if (order.state !== 'pending' || isGone) {
      return undefined;
    }
    const handle = window.setInterval(() => {
      void runVerify(false);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [order.state, isGone, runVerify]);

  if (isGone) {
    return (
      <Alert variant="warning" isInline title="Order no longer exists">
        This certificate order was cancelled or deleted — possibly from another session or by the platform operator.
      </Alert>
    );
  }

  if (order.state === 'issued') {
    return (
      <Alert variant="success" isInline title="Certificate issued">
        {certificate.cn} has been validated and issued. The certificate and its private key are ready to download.
      </Alert>
    );
  }

  if (order.state === 'failed' || order.state === 'cancelled') {
    return (
      <Alert
        variant={order.state === 'failed' ? 'danger' : 'warning'}
        isInline
        title={order.state === 'failed' ? 'Order failed' : 'Order cancelled on ZeroSSL'}
      >
        <Content component="p">
          {order.last_error ??
            (order.state === 'failed'
              ? 'The order could not be completed.'
              : 'The order was cancelled outside the Manager (e.g. from the ZeroSSL dashboard).')}
        </Content>
        <Content component="p">
          Delete this certificate order to clean up the pending entry and its DNS validation records, then place a new
          order if needed.
        </Content>
      </Alert>
    );
  }

  const validationEntries = Object.entries(order.validation ?? {});
  const manualEntries = validationEntries.filter(([, entry]) => entry.managed_zone === null);
  const hasManual = manualEntries.length > 0;

  return (
    <>
      <Alert variant="info" isInline title="Waiting for DNS validation">
        <Content component="p">
          ZeroSSL verifies domain control through CNAME records.{' '}
          {hasManual
            ? 'Some of the domains below are not hosted on Manager-managed DNS — create the highlighted CNAME records at your DNS provider, then use the button below.'
            : 'All validation records were created automatically in your managed DNS zones — the certificate should be issued shortly.'}{' '}
          This page checks the order automatically every 10 seconds.
        </Content>
      </Alert>
      {order.last_error !== null && (
        <Alert variant="warning" isInline title="Last validation attempt" className="pf-v6-u-mt-md">
          {order.last_error}
        </Alert>
      )}
      {checkError !== null && (
        <Alert variant="warning" isInline title="Check failed" className="pf-v6-u-mt-md">
          {checkError}
        </Alert>
      )}
      {validationEntries.length > 0 && (
        <Table aria-label="DNS validation records" variant="compact" className="pf-v6-u-mt-md">
          <Thead>
            <Tr>
              <Th>Domain</Th>
              <Th>CNAME record (host)</Th>
              <Th>Points to (target)</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {validationEntries.map(([domain, entry]) => (
              <Tr key={domain}>
                <Td dataLabel="Domain">{domain}</Td>
                <Td dataLabel="CNAME record (host)">{copyable(entry.cname_host, `validation record host for ${domain}`)}</Td>
                <Td dataLabel="Points to (target)">
                  {copyable(entry.cname_target, `validation record target for ${domain}`)}
                </Td>
                <Td dataLabel="Status">
                  {entry.managed_zone !== null ? (
                    <Label color="green" isCompact>
                      {entry.record_created ? `Created automatically in ${entry.managed_zone}` : `Managed zone ${entry.managed_zone}`}
                    </Label>
                  ) : (
                    <Label color="orange" isCompact>
                      Create this record manually
                    </Label>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
      <div className="pf-v6-u-mt-md">
        <Button variant="primary" onClick={() => void runVerify(true)} isLoading={isChecking} isDisabled={isChecking}>
          {hasManual ? 'I have created the records — check now' : 'Check now'}
        </Button>
      </div>
    </>
  );
}

export default ZerosslOrderPanel;
